/**
 * Attack Handler - D&D 5e Combat Attack Processing
 * 
 * @date_of_change: 2025-09-07
 * @purpose: Extract monster attack function complexity into discrete, testable modules
 * @rule_reference: AGENTS.md - break down complex functions into smaller utilities
 * 
 * This utility replaces the massive 280-line handleAttackAction function with
 * smaller, focused functions that handle different aspects of combat processing.
 * Each function has a single responsibility and can be tested independently.
 */

import NotificationManager from '../NotificationManager';
import RangeCalculator from '../RangeCalculator';
import MultiattackManager from '../../services/MultiattackManager';
import ActionEconomyProcessor from './ActionEconomyProcessor';
import { buildCombatActionCatalog } from './CombatActionCatalog';
import { buildCombatExecutionBundle, resolveCombatTargets } from './CombatExecutionRequestBuilder';
import { collectManualAttackRolls } from './AttackDicePreflight';

// Reject incomplete manual-roll preflights before the combat processor can
// mutate action economy or fall back to an unrelated random/native roll.
// date_of_change: 2026-07-18
/**
 * Resolve attacker speed to numeric feet using canonical sources.
 * @date_of_change: 2025-10-13
 * @rule_reference: AGENTS.md §9 NO LEGACY PATHS OR FALLBACKS — provide explicit inputs to the engine
 * Attempts, in order: token.speed, characterData.speed, monsterData.speed, monsterData.data.speed,
 * characterData.combatStats.speed, token.speedModifiers.walk (if present). Parses strings/objects via
 * ActionEconomyProcessor.parseSpeedFeet. Returns null if no candidate yields a valid numeric speed.
 */
const resolveAttackerSpeedFeet = (attacker) => {
  const candidates = [
    attacker?.speed,
    attacker?.movement,
    attacker?.characterData?.speed,
    attacker?.characterData?.movement,
    attacker?.monsterData?.speed,
    attacker?.monsterData?.movement,
    attacker?.monsterData?.data?.speed,
    attacker?.monsterData?.data?.movement,
    attacker?.characterData?.combatStats?.speed,
    attacker?.speedModifiers?.walk
  ];

  for (const c of candidates) {
    try {
      if (c == null) continue;
      // If the candidate is an object (e.g., {walk:30, fly:60}), try common keys first
      if (typeof c === 'object' && c !== null) {
        const w = c.walk ?? c.ground ?? c.land ?? c.base ?? c.value;
        if (w != null) {
          const v = ActionEconomyProcessor.parseSpeedFeet(w);
          if (Number.isFinite(v)) return v;
        }
      }
      const v = ActionEconomyProcessor.parseSpeedFeet(c);
      if (Number.isFinite(v)) return v;
    } catch (_err) {
      // Ignore and try next candidate
    }
  }
  return null;
};

const resolveAttackerActions = (attacker) => buildCombatActionCatalog(attacker);

/**
 * Attack Validation Module
 * Handles all pre-attack validation logic
 */
export class AttackValidator {
  /**
   * Validate attacker token
   * @date_of_change: 2025-09-17 - CRITICAL FIX: Support monster tokens with nested id fields
   * @purpose: Fix monster combat execution pipeline by handling diverse token data structures
   * @rule_reference: D&D 5e combat requires all creatures to participate in action economy
   */
  static validateAttacker(attacker) {
    // Check for id in multiple possible locations to handle both character and monster tokens
    const attackerId = attacker?.id ||
                      attacker?.monsterData?.id ||
                      attacker?.characterData?.id ||
                      attacker?.data?.id;

    if (!attackerId) {
      console.error('[AttackValidator] Attacker token missing id field:', {
        attacker: attacker,
        name: attacker?.name,
        type: attacker?.type,
        hasTopLevelId: !!attacker?.id,
        hasMonsterDataId: !!attacker?.monsterData?.id,
        hasCharacterDataId: !!attacker?.characterData?.id,
        hasDataId: !!attacker?.data?.id
      });
      NotificationManager.warning('Select an attacker token first.');
      return false;
    }

    console.log('[AttackValidator] ✅ Attacker validation passed:', {
      name: attacker?.name,
      id: attackerId,
      type: attacker?.type
    });
    return true;
  }

  /**
   * Resolve and validate target IDs
   */
  static resolveTargetIds(explicitTargetIds, hoveredToken, selectedTokenId, attackerId) {
    // Choose targets: explicit > hovered/selected fallback
    let targetIds = Array.isArray(explicitTargetIds) ? explicitTargetIds.slice() : [];
    
    if (!targetIds.length) {
      const fallback = hoveredToken || (selectedTokenId !== attackerId ? selectedTokenId : null);
      if (fallback) targetIds = [fallback];
    }
    
    // Validate - filter out invalid and self-targeting
    targetIds = targetIds.filter(id => id && id !== attackerId);
    
    if (!targetIds.length) {
      NotificationManager.warning('Select a valid target.');
      return null;
    }
    
    return targetIds;
  }

  /**
   * Resolve target tokens from IDs
   */
  static resolveTargetTokens(targetIds, tokens) {
    const targets = targetIds.map(id => tokens.find(t => t.id === id)).filter(Boolean);
    
    if (!targets.length) {
      console.error('[AttackHandler] Target token(s) not found in tokens list');
      return null;
    }
    
    return targets;
  }

  /**
   * Validate attack range using D&D 5e rules
   */
  static validateRange(attacker, targets, actionData, gridSize, gridType, feetPerCell) {
    // date_of_change: 2025-10-13 - POLICY: Require explicit grid context
    if (!Number.isFinite(gridSize) || !gridType || !Number.isFinite(feetPerCell)) {
      NotificationManager.error('Missing grid context', 'gridType, feetPerCell, and gridSize are required to validate range');
      return false;
    }

    const battleState = {
      gridSize: gridSize,
      feetPerCell: feetPerCell,
      obstacles: [] // TODO: Add obstacles based on tokens/terrain if needed
    };
    
    const gridOptions = {
      gridType,
      feetPerCell,
      gridSize
    };

    // @date_of_change: 2025-10-10 - Sanitize grid coordinates that may be numeric strings and provide attack kind hint
    const sanitizeGridToken = (t) => {
      if (!t) return t;
      const gxRaw = t.gridX;
      const gyRaw = t.gridY;
      const gx = (typeof gxRaw === 'string') ? (gxRaw.trim().toLowerCase() === 'off grid' ? NaN : Number(gxRaw)) : gxRaw;
      const gy = (typeof gyRaw === 'string') ? (gyRaw.trim().toLowerCase() === 'off grid' ? NaN : Number(gyRaw)) : gyRaw;
      return {
        ...t,
        gridX: Number.isFinite(gx) ? gx : t.gridX,
        gridY: Number.isFinite(gy) ? gy : t.gridY,
        size: t.size || 'Medium'
      };
    };

    // Determine attack type from structured metadata (no free-text heuristics)
    // @date_of_change 2025-10-14 - Align with ActionEconomyProcessor inference
    let attackTypeHint = null;
    if (typeof actionData?.attackType === 'string') {
      const t = actionData.attackType.toLowerCase();
      if (t === 'melee' || t === 'ranged') attackTypeHint = t;
    }
    if (!attackTypeHint && typeof actionData?.attack?.type === 'string') {
      const tt = actionData.attack.type.toLowerCase();
      if (tt.includes('melee')) attackTypeHint = 'melee';
      else if (tt.includes('ranged')) attackTypeHint = 'ranged';
    }
    if (!attackTypeHint && (actionData?.type === 'spell' || actionData?.isSpell)) {
      attackTypeHint = 'ranged';
    }
    if (!attackTypeHint && (Number.isFinite(actionData?.reach) || Number.isFinite(actionData?.attack?.reach))) {
      attackTypeHint = 'melee';
    }
    if (!attackTypeHint && (actionData?.range != null || actionData?.attack?.range != null)) {
      attackTypeHint = 'ranged';
    }

    // Validate each target
    for (const target of targets) {
      try {
        // @date_of_change: 2025-09-11 - CRITICAL DEBUG: Log exact actionData passed to RangeCalculator
        console.log(`[AttackHandler] 🔍 RANGE_VALIDATION_DEBUG: About to validate "${actionData?.name}"`);
        console.log(`[AttackHandler] - Full actionData object:`, actionData);
        console.log(`[AttackHandler] - actionData.type: "${actionData?.type}", actionData.range: "${actionData?.range}"`);
        
        const sanitizedAttacker = sanitizeGridToken(attacker);
        const sanitizedTarget = sanitizeGridToken(target);

        const validation = RangeCalculator.validateAttack(
          sanitizedAttacker,
          sanitizedTarget,
          actionData,
          battleState,
          { ...gridOptions, attackType: attackTypeHint }
        );
        
        if (!validation.valid) {
          const reason = validation.reasons?.join(', ') || 'Range validation failed';
          console.warn(`[AttackHandler] Range validation failed for ${target.name}: ${reason}`);
          // @date_of_change 2025-09-14 - ENHANCED DEBUG: Track exact popup source
          try {
            const diag = RangeCalculator.getEdgeDistanceFeet?.(sanitizedAttacker, sanitizedTarget, gridOptions);
            console.error(`🚨 [AttackHandler] POPUP_SOURCE_1: Cannot attack ${target.name}: ${reason}`, {
              target: sanitizedTarget,
              actionData,
              validation,
              gridOptions,
              diag,
              stackTrace: new Error().stack
            });
          } catch (diagErr) {
            console.error('[AttackHandler] Range diag failed:', diagErr);
          }
          NotificationManager.warning(`Cannot attack ${target.name}: ${reason}`);
          return false;
        }
        
        console.log(`[AttackHandler] Range validation passed for ${target.name}`);
      } catch (rangeError) {
        console.error(`[AttackHandler] Range validation error:`, rangeError);
        NotificationManager.warning(`Range validation failed: ${rangeError.message}`);
        return false;
      }
    }
    
    return true;
  }
}

/**
 * Multiattack Processing Module
 * Handles D&D 5e multiattack sequence detection and setup
 */
export class MultiattackProcessor {
  /**
   * Check if action is a multiattack
   */
  static isMultiattackAction(actionData) {
    if (actionData?.mechanics) return actionData.mechanics.kind === 'multiattack';
    const isMultiattack = actionData.name?.toLowerCase().includes('multiattack') && 
                         (actionData.text || actionData.description || actionData.desc);
    
    console.log(`[AttackHandler] Multiattack check for "${actionData.name}":`, isMultiattack);
    return isMultiattack;
  }

  /**
   * Process multiattack sequence
   */
  static processMultiattack(actionData, attacker, selectedTargets, tokens, setMultiattackSequence, setArmedAction, setTargetingEnabled, gmOverride = null) {
    console.log(`[AttackHandler] Starting multiattack sequence: ${actionData.name}`);
    console.log(`[AttackHandler] Multiattack description: "${actionData.description || actionData.text || actionData.desc}"`);
    
    // Get all available actions and traits for the attacker
    // @date_of_change 2025-09-09 - CRITICAL FIX: Access nested monster data structure
    // Monster JSON files have actions nested under data.actions, need to access correctly
    const attackerActions = resolveAttackerActions(attacker);
    const attackerTraits = attacker.traits || 
                          attacker.monsterData?.data?.traits || attacker.monsterData?.traits ||
                          attacker.characterData?.traits || 
                          attacker.data?.traits || [];
    
    console.log(`[AttackHandler] Available actions for parsing (${attackerActions.length}):`, attackerActions.map(a => a.name));
    console.log(`[AttackHandler] Available traits for parsing (${attackerTraits.length}):`, attackerTraits.map(t => t.name));
    
    // Find the complete multiattack action
    const fullMultiattackAction = attackerActions.find(action => 
      action.name?.toLowerCase() === actionData.name?.toLowerCase()
    );
    
    let componentActions;
    if (fullMultiattackAction) {
      console.log(`[AttackHandler] Found complete multiattack action with attack property:`, !!fullMultiattackAction.attack);
      componentActions = MultiattackManager.resolveMultiattack(fullMultiattackAction, attackerActions, attackerTraits, attacker);
    } else {
      console.warn(`[AttackHandler] Could not find complete multiattack action for: ${actionData.name}`);
      componentActions = MultiattackManager.resolveMultiattack(actionData, attackerActions, attackerTraits, attacker);
    }
    
    console.log(`[AttackHandler] Parsed ${componentActions.length} component actions:`, componentActions.map(a => a.name));
    
    if (componentActions.length === 0) {
      NotificationManager.error(`Multiattack ${actionData.name} has no valid component actions`);
      return false;
    }
    
    // Log multiattack start
    this.logMultiattackStart(attacker, actionData, selectedTargets, tokens, componentActions);
    
    // Set up multiattack sequence
    const multiattackSequenceData = {
      parentAction: actionData.name,
      componentActions: componentActions,
      currentIndex: 0,
      attackerId: attacker.id,
      totalAttacks: componentActions.length,
      gmOverride
    };
    
    console.log(`[AttackHandler] Setting multiattack sequence:`, multiattackSequenceData);
    setMultiattackSequence(multiattackSequenceData);
    
    // @date_of_change 2025-09-09 - CRITICAL FIX: Arm the first component for targeting after setting sequence
    // After creating the multiattack sequence, we need to arm the first component for targeting
    const firstAction = componentActions[0];
    if (firstAction) {
      console.log(`[AttackHandler] Arming first multiattack component for targeting: ${firstAction.name}`);
      setArmedAction({
        attackerId: attacker.id,
        action: firstAction,
        multiTarget: false,
        requiredTargets: 1,
        gmOverride
      });
      setTargetingEnabled(true);
      NotificationManager.info(`Select target for ${firstAction.name} (1/${componentActions.length})`);
    }
    
    return true;
  }

  /**
   * Log multiattack sequence start
   */
  static logMultiattackStart(attacker, actionData, selectedTargets, tokens, componentActions) {
    // @date_of_change 2025-09-13 - REMOVED DUPLICATE LOGGING: ActionEconomyProcessor handles all logging
    // This was causing duplicate combat log entries - ActionEconomyProcessor will log the action
    console.log('[AttackHandler] Multiattack sequence started (logging handled by ActionEconomyProcessor)');
  }
}

// @date_of_change 2025-09-10 - REMOVED DUPLICATE ActionEconomyProcessor CLASS
// This was violating architectural principle: NO DUPLICATE DECLARATIONS
// All action economy processing now handled by the main ActionEconomyProcessor in ./ActionEconomyProcessor.js

/**
 * Targeting Mode Module
 * Handles targeting mode setup and validation
 */
export class TargetingModeProcessor {
  /**
   * Check if action requires targeting mode
   */
  static requiresTargeting(actionData) {
    // Check for actions that need targeting but don't have explicit targets
    const needsTargeting = actionData.range && actionData.range !== 'Self';
    const isAreaEffect = actionData.areaType && actionData.areaSize;
    
    // For now, simple check - can be enhanced with more sophisticated logic
    return needsTargeting && !isAreaEffect;
  }

  /**
   * Enter targeting mode
   */
  static enterTargetingMode(actionData, attacker, setArmedAction, setTargetingEnabled, gmOverride = null) {
    console.log(`[AttackHandler] Entering targeting mode for: ${actionData.name}`);
    
    // @date_of_change 2025-09-09 - CRITICAL FIX: Include attackerId in armed action
    const armedActionData = {
      action: actionData,
      attackerId: attacker.id,
      timestamp: Date.now(),
      gmOverride
    };
    
    console.log(`[AttackHandler] Armed action data:`, armedActionData);
    setArmedAction(armedActionData);
    setTargetingEnabled(true);
    
    NotificationManager.info(`Select targets for ${actionData.name}`);
    return true;
  }
}

/**
 * Main Attack Handler
 * Orchestrates the attack processing workflow
 */
export class AttackHandler {
  /**
   * Process a complete attack action
   * This is the main entry point that replaces the massive handleAttackAction function
   */
  static async processAttack({
    attacker,
    actionData,
    explicitTargetIds,
    // Dependencies
    hoveredToken,
    selectedTokenId, 
    tokens,
    gridSize,
    gridType,
    feetPerCell,
    rulesetExecutionState = {},
    // Action economy
    consumeActionEconomy,
    // Targeting state
    setArmedAction,
    setTargetingEnabled,
    // Multiattack state
    setMultiattackSequence,
    selectedTargets,
    // Flags
    willEnterTargetingMode = false,
    actionEconomyAlreadyConsumed = false,
    gmOverride = null
  }) {
    console.log('🗡️ [ATTACK:START]', { 
      attackerName: attacker?.name,
      actionName: actionData?.name, 
      targetIds: explicitTargetIds,
      timestamp: new Date().toISOString()
    });

    // @date_of_change 2025-09-09 - Enhanced debugging for targeting mode detection
    console.log('🎯 [TARGETING:DEBUG]', {
      actionName: actionData?.name,
      actionRange: actionData?.range,
      rangeNotSelf: actionData?.range !== 'Self',
      selectedTargetsLength: selectedTargets?.length,
      willEnterTargetingMode,
      explicitTargetIds: explicitTargetIds?.length || 0,
      actionEconomyAlreadyConsumed
    });

    // Step 1: Validate attacker
    if (!AttackValidator.validateAttacker(attacker)) {
      return { success: false, reason: 'Invalid attacker' };
    }

    // Step 2: Normalize inputs required by ActionEconomyProcessor (explicit speed)
    // @date_of_change: 2025-10-13 - Provide explicit attacker.speed in feet when derivable from canonical sources
    const normalizedSpeed = resolveAttackerSpeedFeet(attacker);
    const attackerForProcessor = normalizedSpeed != null ? { ...attacker, speed: normalizedSpeed } : attacker;

    // Step 2b: Pre-collect manual attack/damage rolls inline (Action Targeting panel), replacing
    // the native window.prompt for the common single-target case.
    // date_of_change: 2026-07-12 - see collectManualAttackRolls doc comment for scope/limits.
    let manualRollOverrides = null;
    try {
      const { targets: previewTargets } = resolveCombatTargets({ targetIds: explicitTargetIds, tokens });
      if (previewTargets.length === 1 && !actionData.mechanics) {
        const previewCombatState = { tokens, gridSize, gridType, feetPerCell, ...rulesetExecutionState };
        const collected = await collectManualAttackRolls(attackerForProcessor, previewTargets[0], actionData, previewCombatState);
        if (collected) {
          manualRollOverrides = { __manualRolls: collected.manualRolls, __manualRollQueue: collected.manualRollQueue };
        }
      }
    } catch (error) {
      // Manual roll collection is a preflight. Never fall through to the legacy
      // synchronous prompt/random path, which could spend an action after cancel.
      // date_of_change: 2026-07-18
      const cancelled = error?.code === 'ACTION_CANCELLED';
      if (cancelled) {
        NotificationManager.info('Attack canceled', 'No attack or action economy was spent.');
      } else {
        NotificationManager.error('Attack roll unavailable', error?.message || 'Manual roll input could not be validated.');
      }
      return {
        success: false,
        cancelled,
        code: error?.code || 'MANUAL_ROLL_FAILED',
        reason: error?.message || 'Manual roll preflight failed'
      };
    }

    // Step 3: Process through ActionEconomyProcessor for Phase One integration
    // @date_of_change 2025-09-10 - PHASE ONE: Full integration with ActionEconomyProcessor
    // All attacks must go through the processor for proper validation and logging
    const attackExecution = buildCombatExecutionBundle({
      attackerId: attackerForProcessor?.id,
      attacker: attackerForProcessor,
      actionData,
      targetIds: explicitTargetIds
    }, {
      attacker: attackerForProcessor,
      tokens,
      gridSize,
      gridType,
      feetPerCell,
      actionEconomyConsumed: actionEconomyAlreadyConsumed,
      extraCombatState: {
        // The BattleRoom commits the canonical token patch before emitting the
        // audit event. The processor must therefore return an unlogged result.
        // date_of_change: 2026-07-18
        deferAudit: true,
        ...rulesetExecutionState
      },
      ...(manualRollOverrides ? { actionDataOverrides: manualRollOverrides } : {})
    });
    const {
      processorRequest: attackRequest,
      combatState,
      targets: attackTargets,
      unresolvedTargetIds
    } = attackExecution;

    if ((explicitTargetIds?.length || 0) > 0 && unresolvedTargetIds.length > 0) {
      return {
        success: false,
        reason: 'Unable to resolve selected target(s)',
        errors: unresolvedTargetIds.map((targetId) => `Missing target token: ${targetId}`)
      };
    }

    // @date_of_change 2025-09-17 - CRITICAL FIX: Process AoE actions even when entering targeting mode
    // AoE abilities that have selected targets should still go through ActionEconomyProcessor
    const hasTargetsForProcessing = explicitTargetIds && explicitTargetIds.length > 0;
    const shouldProcessAction = !willEnterTargetingMode || hasTargetsForProcessing;

    console.log('🚀 [PROCESS_ACTION_DEBUG]', {
      willEnterTargetingMode,
      hasTargetsForProcessing,
      shouldProcessAction,
      targetCount: explicitTargetIds?.length || 0,
      explicitTargetIds: explicitTargetIds,
      actionName: actionData?.name
    });

    let processorResult = null;

    if (shouldProcessAction) {
      try {
        console.log('🚀 [CALLING_ACTION_ECONOMY_PROCESSOR]', {
          actionName: actionData.name,
          targetCount: attackTargets.length,
          attackRequest
        });

        processorResult = await ActionEconomyProcessor.processAction(
          attackRequest,
          combatState,
          gmOverride
        );

        console.log('🚀 [ACTION_ECONOMY_PROCESSOR_RESULT]', processorResult);

        if (!processorResult.success) {
          // @date_of_change 2025-09-14 - ENHANCED DEBUG: Log detailed validation errors
          console.error('[AttackHandler] VALIDATION_FAILURE_DEBUG:', {
            error: processorResult.error,
            errors: processorResult.errors,
            attackerData: attacker,
            actionData: actionData,
            processorResult: processorResult
          });

          // @date_of_change 2025-09-14 - IMPROVED UX: Handle range validation failures gracefully
          const isRangeError = processorResult.errors?.some(error =>
            error.includes('out of range') ||
            error.includes('out of melee range') ||
            error.includes('Target out') ||
            error.includes('Range validation failed')
          );

          if (isRangeError) {
            // For range errors, provide better UX - clear target and allow reselection
            console.log('[AttackHandler] Range validation failed - allowing target reselection');
            return {
              success: false,
              reason: 'Target out of range',
              errors: processorResult.errors,
              allowTargetReselection: true, // Flag to indicate graceful handling
              rangeError: true
            };
          }

          return {
            success: false,
            reason: processorResult.error,
            errors: processorResult.errors
          };
        }

        // @date_of_change 2025-09-17 - CRITICAL SUCCESS: Action was processed successfully
        console.log('✅ [ACTION_PROCESSED_SUCCESSFULLY]', {
          actionName: actionData.name,
          targetCount: attackTargets.length,
          willEnterTargetingMode
        });

        // @date_of_change 2025-09-17 - CRITICAL FIX: For AoE abilities with targets, return success immediately
        // AoE abilities that went through ActionEconomyProcessor don't need individual target validation
        if (hasTargetsForProcessing && willEnterTargetingMode) {
          console.log('✅ [AOE_EXECUTION_COMPLETE]', {
            actionName: actionData.name,
            targetCount: attackTargets.length,
            reason: 'AoE ability processed through ActionEconomyProcessor'
          });

          return {
            success: true,
            attacker,
            targets: attackTargets,
            actionData,
            aoeComplete: true, // Flag to indicate AoE was processed
            reason: 'AoE ability executed successfully',
            processorResult,
            processorRequest: attackRequest,
            combatState
          };
        }

      } catch (error) {
        console.error('[AttackHandler] ActionEconomyProcessor error:', error);
        return {
          success: false,
          reason: `Action processing failed: ${error.message}`
        };
      }
    }

    // Step 3: Check for multiattack (now integrated with ActionEconomyProcessor)
    if (MultiattackProcessor.isMultiattackAction(actionData)) {
      // For multiattacks, the main action was consumed by ActionEconomyProcessor
      // Now we set up the multiattack sequence without additional economy consumption
      const multiattackSuccess = MultiattackProcessor.processMultiattack(
        actionData, 
        attacker, 
        selectedTargets, 
        tokens, 
        setMultiattackSequence,
        setArmedAction,
        setTargetingEnabled,
        gmOverride
      );
      
      if (multiattackSuccess) {
        return {
          success: true,
          multiattack: true,
          processorResult,
          processorRequest: attackRequest,
          combatState,
          attacker,
          targets: attackTargets,
          actionData
        };
      } else {
        return { success: false, reason: 'Multiattack processing failed' };
      }
    }

    if (processorResult && processorResult.success) {
      // @date_of_change: 2025-10-12 - Prevent legacy double execution by returning processor results directly
      // rule_reference: AGENTS.md (separate rules engine from presentation; avoid duplicate state mutation)
      return {
        success: true,
        processedByActionEconomy: true,
        processorResult,
        attacker,
        targets: attackTargets,
        actionData,
        processorRequest: attackRequest,
        combatState
      };
    }

    // Step 4: Check if targeting mode is needed
    // @date_of_change 2025-09-10 - CRITICAL FIX: Don't re-enter targeting after successful execution
    // If we have targets and the action was processed successfully, skip targeting check
    const hasTargetsForExecution = explicitTargetIds && explicitTargetIds.length > 0;
    const needsTargeting = willEnterTargetingMode || (!hasTargetsForExecution && TargetingModeProcessor.requiresTargeting(actionData));
    
    if (needsTargeting) {
      TargetingModeProcessor.enterTargetingMode(
        actionData,
        attacker,
        setArmedAction,
        setTargetingEnabled,
        gmOverride
      );
      
      // @date_of_change 2025-09-09 - CRITICAL FIX: Return proper result structure for targeting mode
      return {
        success: true,
        targetingMode: true,
        reason: 'Entered targeting mode'
      };
    }

    // Step 5: Resolve and validate targets
    const targetIds = AttackValidator.resolveTargetIds(
      explicitTargetIds,
      hoveredToken,
      selectedTokenId,
      attacker.id
    );
    
    if (!targetIds) {
      return { success: false, reason: 'No valid targets' };
    }

    const targets = AttackValidator.resolveTargetTokens(targetIds, tokens);
    if (!targets) {
      return { success: false, reason: 'Target tokens not found' };
    }

    // Step 6: Validate attack range
    const rangeValid = AttackValidator.validateRange(
      attacker,
      targets,
      actionData,
      gridSize,
      gridType,
      feetPerCell
    );
    
    if (!rangeValid) {
      return { success: false, reason: 'Range validation failed' };
    }

    // Step 7: Execute attack (this would continue with the existing attack execution logic)
    console.log('🗡️ [ATTACK:VALIDATED] Ready to execute attack on targets:', targets.map(t => t.name));
    
    return {
      success: true,
      attacker,
      targets,
      actionData,
      readyForExecution: true
    };
  }
}
